import { Component, computed, effect, inject, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { PDFExportComponent, PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import { LoaderModule, SkeletonModule } from '@progress/kendo-angular-indicators';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { GridModule, ExcelModule, GridDataResult } from '@progress/kendo-angular-grid';
import { SlaAdherenceDetailsService } from 'src/app/features/customer/msp-portal/analytics/components/sla-adherence-card/services/slaadherencedetails.service';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AsyncPipe, formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { SuspectUsersComponent } from './components/suspect-users/suspect-users.component';
import { TopDetectionsComponent } from './components/top-detections/top-detections.component';
import { AdrManualComponent } from './components/adr-manual/adr-manual.component';
import { UnifiedGeoMapComponent } from './components/unified-geo-map/unified-geo-map.component';
import { SuspectAssetsComponent } from './components/suspect-assets/suspect-assets.component';
import { DistillationFunnelComponent } from '../../../msp-portal/analytics/components/distillation-funnel/distillation-funnel.component';
import { EventsEscalationsDialogComponent } from './components/events-escalations-dialog/events-escalations-dialog.component';
import { BaseComponent } from '../../../../../core/shared/common/base.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SeriesClickEvent } from '@progress/kendo-angular-charts';
import { infoCircleIcon, moreVerticalIcon, sparklesIcon, filterIcon, exportIcon } from '@progress/kendo-svg-icons';
import { filter, finalize, forkJoin } from 'rxjs';
import { NotificationService } from '@progress/kendo-angular-notification';
import { ReportJobService, ReportJobConfig } from 'src/app/core/services/reporting/report-job.service';
import {
  AnalyticsService,
  AlertStatSummaryResponse,
  SlaAdherenceResponse,
  ticketTrendsResponse,
  QueryType,
} from '../../../../../core/services/analytics.service';
import { EventsToEscalationsBarComponent } from './components/events-to-escalations-bar/events-to-escalations-bar.component';
import { DispositionStatsComponent } from './components/disposition-stats/disposition-stats.component';
import { TopMttdDetectionsComponent } from './components/top-mttd-detections/top-mttd-detections.component';
import { TicketDispositionGridComponent } from './components/ticket-disposition-grid/ticket-disposition-grid.component';
import { TicketCategoryPerformanceComponent } from './components/ticket-category-performance/ticket-category-performance.component';
import { AutomatedResponseUsageComponent } from './components/automated-response-usage/automated-response-usage.component';
import { TicketClosureOutcomesComponent } from './components/ticket-closure-outcomes/ticket-closure-outcomes.component';
import { BaseAnalyticsPageComponent } from 'src/app/core/components/base-analytics-page/base-analytics-page.component';
import {
  AnalyticsFilterComponent,
  FilterConfig,
  FilterState,
  TimeRange,
} from 'src/app/core/components/analytics-filter';
import { SocDataService, SocAnalyticsParams } from './services/soc-data.service';
import {
  EventsVsEscalationsChartData,
  MttdStatData,
  MttrStatData,
  TopMttdDetectionsData,
} from 'src/app/core/models/analytics/soc-models';
import { ProductService } from 'src/app/features/customer/msp-portal/analytics/services/product.service';
import { Product } from 'src/app/core/models/entities';
import { CustomerDispositionGridComponent } from './components/customer-disposition-grid/customer-disposition-grid.component';
import { AlertDataDialogComponent } from 'src/app/core/components/alert-data-dialog';
import { TicketDataDialogComponent } from 'src/app/core/components/ticket-data-dialog';
import { AlertDataDialogConfig } from 'src/app/core/models/dialog-config';
import { TicketDataDialogConfig } from 'src/app/core/models/dialog-config';
import { checkIsDemoOrg } from 'src/app/core/models/organizations';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-soc-v2',
  standalone: true,
  imports: [
    DropDownListModule,
    DateInputsModule,
    ButtonsModule,
    PDFExportModule,
    LabelModule,
    LayoutModule,
    SuspectUsersComponent,
    SuspectAssetsComponent,
    UnifiedGeoMapComponent,
    RouterModule,
    TopDetectionsComponent,
    AdrManualComponent,
    IconsModule,
    TooltipsModule,
    DistillationFunnelComponent,
    EventsToEscalationsBarComponent,
    DispositionStatsComponent,
    TopMttdDetectionsComponent,
    TicketDispositionGridComponent,
    TicketCategoryPerformanceComponent,
    AutomatedResponseUsageComponent,
    TicketClosureOutcomesComponent,
    BaseAnalyticsPageComponent,
    AnalyticsFilterComponent,
    LoaderModule,
    SkeletonModule,
    DialogsModule,
    GridModule,
    ExcelModule,
    AsyncPipe,
    CustomerDispositionGridComponent,
    EventsEscalationsDialogComponent,
    AlertDataDialogComponent,
    TicketDataDialogComponent,
  ],
  templateUrl: './soc.component.html',
  styleUrl: './soc.component.scss',
  providers: [AnalyticsService, SocDataService, ProductService, SlaAdherenceDetailsService],
})
export class SocV2Component extends BaseComponent {
  @ViewChild('socPdf') socPdf!: PDFExportComponent;

  public productService = inject(ProductService);
  private route = inject(ActivatedRoute);
  private analyticsService = inject(AnalyticsService);
  private socDataService = inject(SocDataService);
  private http = inject(HttpClient);
  private reportJobService = inject(ReportJobService);
  private notificationService = inject(NotificationService);

  public isTicketExporting = signal(false);

  public isAlertExporting = signal(false);

  // SLA details dialog functionality
  public readonly slaGridService = inject(SlaAdherenceDetailsService);
  public showSlaPopup = signal(false);

  // Icons
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;
  public sparklesIcon: SVGIcon = sparklesIcon;
  public filterIcon: SVGIcon = filterIcon;
  public exportIcon: SVGIcon = exportIcon;

  // Expose QueryType enum for template use
  public QueryType = QueryType;

  // Filter signals and config
  public selectedTimeRange = signal<TimeRange>({ text: 'Last 30 days', value: '30d' });
  public startDate = signal<Date | null>(new Date(new Date().setDate(new Date().getDate() - 30)));
  public endDate = signal<Date | null>(new Date());
  public selectedTools = signal<string[]>([]);
  selectedServiceGroup = signal<string>('');
  products = signal<Product[]>([]);

  public filterConfig: FilterConfig = {
    showTimeRange: true,
    showCustomerFilters: false,
    showServiceFilters: false,
    showToolFilters: true,
    showApplyButton: true,
    showResetButton: false,
  };
  // Formatted date range with proper timezone for child components
  public dateRangeArray: string[] = ['2023-03-01T00:00:00.000Z', '2023-03-31T23:59:59.999Z'];
  // Dates - UPDATED: startDate and endDate are now signals defined above
  public startDateString: string = '';
  public endDateString: string = '';

  public organizationId: number | null = null;

  public eventsVsEscalationsChartData = signal<EventsVsEscalationsChartData>({
    chart_data: [],
    current_reduction_percentage: 0,
    previous_reduction_percentage: 0,
    reduction_percentage_change: 0,
  });
  public destinationMapData = signal<any>(null);
  public sourceMapData = signal<any>(null);
  public topSuspectUsersData = signal<any>(null);
  public topUserDetailData = signal<any>([]);
  public topUserDetailLoading = signal<boolean>(false);
  public topSuspectAssetsData = signal<any>(null);
  public topAssetDetailData = signal<any>(null);
  public topAssetDetailLoading = signal<boolean>(false);
  public outcomeStatusData = signal<any>(null);
  public dispositionData = signal<any>(null);
  public topDetectionsData = signal<any>(null);
  public adrManualData = signal<any>(null);
  public mttdLineGraphData = signal<any>(null);
  public mttrLineGraphData = signal<any>(null);
  public ticketClosureData = signal<any>(null);
  public topMttdDetectionsData = signal<TopMttdDetectionsData[]>([]);
  public slaAdherenceData = signal<SlaAdherenceResponse>(new SlaAdherenceResponse());
  public alertStatSummary = signal(new AlertStatSummaryResponse());
  public ticketTrends = signal(new ticketTrendsResponse());
  public range = { start: null, end: null };
  public selectedDateRange = signal<string[]>([]);
  public averageMttd = 0;
  public averageMttr = 0;
  public mttdStatData = signal<MttdStatData>(new MttdStatData());
  public mttrStatData = signal<MttrStatData>(new MttrStatData());
  public ticketsDispositionData = signal<any[]>([]);
  public ticketCategoryPerformanceData = signal<any[]>([]);
  public automatedResponseUsageData = signal<any[]>([]);
  public customerDispositionData = signal<any[]>([]);
  public ticketClosureOutcomesData = signal<any[]>([]);

  // Alert Data Dialog state
  public alertDialogConfig = signal<AlertDataDialogConfig | null>(null);
  public showAlertDialog = signal(false);

  // Ticket Data Dialog state
  public ticketDialogConfig = signal<TicketDataDialogConfig | null>(null);
  public showTicketDialog = signal(false);

  // Events vs Escalations -> DataTable dialog state
  public showEventsEscalationsDialog = signal(false);

  // Define visible columns for SOC v2 distillation funnel dialogs
  public socAlertVisibleColumns: string[] = [
    'event_name',
    'customer',
    'case_id',
    'source_ip',
    'destination_ip',
    'case_closed_on',
    'case_severity',
    'zoho_ticket_number',
    'closure_reason',
    'mitre_tactic',
    'event_occurred_on',
    'alert_received_on',
    'case_created_on',
    'case_tt_closed_min',
    'triage_type',
    'case_source',
    'close_reason_justification',
    'tracking_id',
    'sla_exceeded',
    'tags',
  ];

  public socTicketVisibleColumns: string[] = [
    'customer',
    'request_id',
    'created_time',
    'subject',
    'priority',
    'ticket_source',
    'ticket_status',
    'customer_defined_status',
    'sla_violation_type',
    'ticket_closed_time',
    'mean_time_to_resolve',
  ];

  constructor() {
    super();
    this.initEffect();
    // Initialize date string properties with current signal values
    this.updateDateStrings();
  }

  /**
   * Queue a server-side CSV export for the currently open ticket dialog.
   * Resolves the report template on demand from the report type key carried by
   * the open dialog's config, so any ticket-based dialog (MTTR, etc.) is supported.
   */
  public triggerTicketExport(): void {
    const reportKey = this.ticketDialogConfig()?.reportTypeKey ?? null;
    const orgId = this.organizationId;
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!reportKey || !orgId || !startDate || !endDate) {
      this.notificationService.show({
        content: 'Unable to export: missing organization or date range.',
        type: { style: 'warning', icon: true },
        position: { horizontal: 'right', vertical: 'top' },
        animation: { type: 'fade', duration: 400 },
        hideAfter: 5000,
      });
      return;
    }

    this.isTicketExporting.set(true);

    const url = `${environment.apiUrl}/v2/reporting/api/report-types/?$filter=key eq '${reportKey}'`;
    this.http.get<{ value: any[] }>(url).pipe(
      map(response => response.value?.[0]?.report_template?.id ?? null)
    ).subscribe({
      next: (templateId) => {
        if (!templateId) {
          this.isTicketExporting.set(false);
          this.notificationService.show({
            content: 'CSV export is not available for this view yet.',
            type: { style: 'warning', icon: true },
            position: { horizontal: 'right', vertical: 'top' },
            animation: { type: 'fade', duration: 400 },
            hideAfter: 5000,
          });
          return;
        }

        const startUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
        const endUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999));

        const securityTool = this.getSecurityToolsParameter();
        const config: ReportJobConfig = {
          organizations: [orgId],
          primary_organization_id: orgId,
          report_template_id: templateId,
          start_date: formatDate(startUTC, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
          end_date: formatDate(endUTC, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
          delivery_method: 'download',
          report_params: {
            filter_type: QueryType.Filtered,
            ...(securityTool ? { security_tool: securityTool } : {}),
          },
        };

        this.reportJobService.createReportJob(config).subscribe({
          next: () => {
            this.isTicketExporting.set(false);
            this.notificationService.show({
              content: 'Export queued. You will find the CSV in the Reports history once generated.',
              type: { style: 'success', icon: true },
              position: { horizontal: 'right', vertical: 'top' },
              animation: { type: 'fade', duration: 400 },
              closable: true,
              hideAfter: 5000,
            });
          },
          error: () => {
            this.isTicketExporting.set(false);
            this.notificationService.show({
              content: 'Failed to queue export. Please try again.',
              type: { style: 'error', icon: true },
              position: { horizontal: 'right', vertical: 'top' },
              animation: { type: 'fade', duration: 400 },
              hideAfter: 5000,
            });
          },
        });
      },
      error: () => {
        this.isTicketExporting.set(false);
        this.notificationService.show({
          content: 'Failed to queue export. Please try again.',
          type: { style: 'error', icon: true },
          position: { horizontal: 'right', vertical: 'top' },
          animation: { type: 'fade', duration: 400 },
          hideAfter: 5000,
        });
      },
    });
  }

  /**
   * Queue a server-side CSV export for whichever page-level alert dialog is
   * currently open (MTTD or SLA). Resolves the report template on demand from
   * the report type key carried by the open dialog's config.
   */
  public triggerAlertExport(): void {
    const reportKey = this.alertDialogConfig()?.reportTypeKey ?? null;
    const orgId = this.organizationId;
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!reportKey || !orgId || !startDate || !endDate) {
      this.notificationService.show({
        content: 'Unable to export: missing organization or date range.',
        type: { style: 'warning', icon: true },
        position: { horizontal: 'right', vertical: 'top' },
        animation: { type: 'fade', duration: 400 },
        hideAfter: 5000,
      });
      return;
    }

    this.isAlertExporting.set(true);

    const url = `${environment.apiUrl}/v2/reporting/api/report-types/?$filter=key eq '${reportKey}'`;
    this.http.get<{ value: any[] }>(url).pipe(
      map(response => response.value?.[0]?.report_template?.id ?? null)
    ).subscribe({
      next: (templateId) => {
        if (!templateId) {
          this.isAlertExporting.set(false);
          this.notificationService.show({
            content: 'CSV export is not available for this view yet.',
            type: { style: 'warning', icon: true },
            position: { horizontal: 'right', vertical: 'top' },
            animation: { type: 'fade', duration: 400 },
            hideAfter: 5000,
          });
          return;
        }

        const startUTC = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0));
        const endUTC = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999));

        const securityTool = this.getSecurityToolsParameter();
        const config: ReportJobConfig = {
          organizations: [orgId],
          primary_organization_id: orgId,
          report_template_id: templateId,
          start_date: formatDate(startUTC, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
          end_date: formatDate(endUTC, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
          delivery_method: 'download',
          report_params: {
            filter_type: QueryType.Filtered,
            ...(securityTool ? { security_tool: securityTool } : {}),
          },
        };

        this.reportJobService.createReportJob(config).subscribe({
          next: () => {
            this.isAlertExporting.set(false);
            this.notificationService.show({
              content: 'Export queued. You will find the CSV in the Reports history once generated.',
              type: { style: 'success', icon: true },
              position: { horizontal: 'right', vertical: 'top' },
              animation: { type: 'fade', duration: 400 },
              closable: true,
              hideAfter: 5000,
            });
          },
          error: () => {
            this.isAlertExporting.set(false);
            this.notificationService.show({
              content: 'Failed to queue export. Please try again.',
              type: { style: 'error', icon: true },
              position: { horizontal: 'right', vertical: 'top' },
              animation: { type: 'fade', duration: 400 },
              hideAfter: 5000,
            });
          },
        });
      },
      error: () => {
        this.isAlertExporting.set(false);
        this.notificationService.show({
          content: 'Failed to queue export. Please try again.',
          type: { style: 'error', icon: true },
          position: { horizontal: 'right', vertical: 'top' },
          animation: { type: 'fade', duration: 400 },
          hideAfter: 5000,
        });
      },
    });
  }

  private initEffect() {
    effect(() => {
      this.route.queryParams
        .pipe(
          filter((params) => params['organizationId']),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: (params) => {
            this.organizationId = +params['organizationId'];
            // Use queueMicrotask to defer signal writes outside of the effect
            queueMicrotask(() => {
              // Load products for Security Tools filter
              this.loadProducts();
              this.applyFilters();
            });
          },
          error: (error) => {
            console.error('Error processing query parameters:', error);
          },
        });
    });
  }

  onSuspectUsersSeriesClick($event: SeriesClickEvent) {
    // Validate that we have data before processing click
    if (!this.topSuspectUsersData() || this.topSuspectUsersData().length === 0) {
      return;
    }

    // Use the actual date signals instead of the empty selectedDateRange
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!startDate || !endDate) {
      console.warn('Start date or end date is not set');
      return;
    }

    // Set loading state WITHOUT clearing data
    this.topUserDetailLoading.set(true);

    const params: SocAnalyticsParams = {
      alert_received_on__range: `${startDate.toISOString()},${endDate.toISOString()}`,
      username__icontains: $event.category,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    this.socDataService
      .fetchTopUserDetail(this.organizationId!, params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.topUserDetailData.set(response);
          this.topUserDetailLoading.set(false);
        },
        error: (error) => {
          console.error('Error fetching Top User Detail:', error);
          this.topUserDetailLoading.set(false);
        },
      });
  }

  filteredProductsByService = computed(() => {
    // For SOC component, show all products without service group filtering
    return this.products();
  });

  onTopDetecttionsCaseEscalatedCheckboxChanged($event: boolean) {
    // Use the actual date signals instead of the empty selectedDateRange
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!startDate || !endDate) {
      console.warn('Start date or end date is not set');
      return;
    }

    const params: SocAnalyticsParams = {
      alert_received_on__range: `${startDate.toISOString()},${endDate.toISOString()}`,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      is_escalated: $event ? $event : null,
    };

    this.socDataService
      .fetchTopDetectionsData(this.organizationId!, params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response) {
            this.topDetectionsData.set(response);
          }
        },
        error: (error) => {
          console.error('Error fetching top detections data with escalation filter:', error);
        },
      });
  }

  onSuspectAssetsSeriesClick($event: SeriesClickEvent) {
    // Validate that we have data before processing click
    if (!this.topSuspectAssetsData() || this.topSuspectAssetsData().length === 0) {
      return;
    }

    // Use the actual date signals instead of the empty selectedDateRange
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!startDate || !endDate) {
      console.warn('Start date or end date is not set');
      return;
    }

    // Set loading state WITHOUT clearing data
    this.topAssetDetailLoading.set(true);

    const params: SocAnalyticsParams = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      source_ip: $event.category,
    };

    this.socDataService
      .fetchTopAssetDetailData(this.organizationId!, params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.topAssetDetailData.set(response);
          this.topAssetDetailLoading.set(false);
        },
        error: (error) => {
          console.error('Error fetching Top Asset Detail:', error);
          this.topAssetDetailLoading.set(false);
        },
      });
  }

  // Progressive loading state signals - loads data in phases from top to bottom
  public isCriticalDataLoading = signal(false); // Phase 1: Distillation funnel + Events vs Escalations
  public isPhase2Loading = signal(false); // Phase 2: Disposition Stats + Top MTTD + Disposition Grids
  public isPhase3Loading = signal(false); // Phase 3: Automated Response + Ticket Closure + Category Performance
  public isPhase4Loading = signal(false); // Phase 4: Top Detections + Suspect Users/Assets + Geo Distribution

  applyFilters() {
    const value = this.selectedTimeRange().value;

    if (value === '7d') {
      this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 7)));
      this.endDate.set(new Date());
    } else if (value === '30d') {
      this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 30)));
      this.endDate.set(new Date());
    } else if (value === '90d') {
      this.startDate.set(new Date(new Date().setDate(new Date().getDate() - 90)));
      this.endDate.set(new Date());
    }

    if (!this.organizationId || !this.startDate() || !this.endDate()) {
      console.warn('Missing required data for analytics:', {
        organizationId: this.organizationId,
        startDate: this.startDate(),
        endDate: this.endDate(),
      });
      return;
    }

    // Update date strings - normalize to day boundaries in UTC
    // We should probably do the same shit across the board.
    const startDate = new Date(this.startDate()!);
    startDate.setUTCHours(0, 0, 0, 0);
    this.startDateString = startDate.toISOString();

    const endDate = new Date(this.endDate()!);
    endDate.setUTCHours(23, 59, 59, 999);
    this.endDateString = endDate.toISOString();

    // Create base parameters
    const dateRange = `${this.startDateString},${this.endDateString}`;
    const params: SocAnalyticsParams = {
      alert_received_on__range: dateRange,
      created_time__range: dateRange,
      startDate: this.startDateString,
      endDate: this.endDateString,
      type: QueryType.Filtered,
    };

    // Add security_tool parameter if tools are selected
    const securityTool = this.getSecurityToolsParameter();
    if (securityTool) {
      (params as any).security_tool = securityTool;
    }

    // Reset all loading states for progressive loading
    this.isCriticalDataLoading.set(true);
    this.isPhase2Loading.set(true);
    this.isPhase3Loading.set(true);
    this.isPhase4Loading.set(true);
    forkJoin({
      eventsVsEscalations: this.socDataService.fetchEventsVsEscalationsChartData(this.organizationId!, params),
      alertStatSummary: this.socDataService.fetchAlertStatSummary(
        this.organizationId!,
        QueryType.Filtered,
        params.startDate,
        params.endDate,
        params.security_tool
      ),
      ticketTrends: this.socDataService.fetchTicketTrends(
        this.organizationId!,
        QueryType.Filtered,
        params.startDate,
        params.endDate,
        params.security_tool
      ),
      slaAdherenceData: this.socDataService.fetchSlaVoilationsData(this.organizationId!, {
        startDate: params.startDate,
        endDate: params.endDate,
        security_tool: params.security_tool,
      }),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isCriticalDataLoading.set(false);
          // Start Phase 2 loading after Phase 1 completes
          this.loadPhase2Data(params);
        })
      )
      .subscribe({
        next: (response) => {
          // Set critical data - eventsVsEscalations and distillation funnel data
          this.eventsVsEscalationsChartData.set(response.eventsVsEscalations);
          this.alertStatSummary.set(response.alertStatSummary || new AlertStatSummaryResponse());
          this.ticketTrends.set(response.ticketTrends || new ticketTrendsResponse());
          this.slaAdherenceData.set(response.slaAdherenceData || new SlaAdherenceResponse());
        },
        error: (error) => {
          console.error('Error loading critical SOC analytics:', error);
        },
      });
  }

  /**
   * Phase 2: Disposition Stats + Top MTTD Detections + Disposition Grids
   * Loads key metrics and disposition data
   */
  private loadPhase2Data(params: SocAnalyticsParams): void {
    this.isPhase2Loading.set(true);

    forkJoin({
      mttdLineGraphData: this.socDataService.fetchMttdLineGraphData(this.organizationId!, params),
      mttrLineGraphData: this.socDataService.fetchMttrLineGraphData(this.organizationId!, params),
      mttdStatData: this.socDataService.fetchMttdStatData(this.organizationId!, params),
      mttrStatData: this.socDataService.fetchMttrStatData(this.organizationId!, params),
      topMttdDetectionsData: this.socDataService.fetchTopMttdDetectionsData(this.organizationId!, params),
      ticketsDispositionData: this.socDataService.fetchTicketsDispositionData(this.organizationId!, params),
      customerDispositionData: this.socDataService.fetchCustomerDispositionData(this.organizationId!, params),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isPhase2Loading.set(false);
          // Start Phase 3 after Phase 2 completes
          this.loadPhase3Data(params);
        })
      )
      .subscribe({
        next: (response) => {
          this.mttdLineGraphData.set(response.mttdLineGraphData);
          this.mttrLineGraphData.set(response.mttrLineGraphData);
          this.mttdStatData.set(response.mttdStatData);
          this.mttrStatData.set(response.mttrStatData);
          this.averageMttd = response.mttdStatData?.current_average_mttd || 0;
          this.averageMttr = response.mttrStatData?.current_average_mttr_hours || 0;
          this.topMttdDetectionsData.set(response.topMttdDetectionsData || []);
          this.ticketsDispositionData.set(response.ticketsDispositionData || []);
          this.customerDispositionData.set(response.customerDispositionData || []);
        },
        error: (error) => {
          console.error('Error loading Phase 2 data:', error);
          // Continue to Phase 3 even if Phase 2 fails
          this.loadPhase3Data(params);
        },
      });
  }

  /**
   * Phase 3: Automated Response + Ticket Closure + Category Performance + ADR
   * Loads ticket-related analytics
   */
  private loadPhase3Data(params: SocAnalyticsParams): void {
    this.isPhase3Loading.set(true);

    forkJoin({
      automatedResponseUsageData: this.socDataService.fetchAutomatedResponseUsageData(this.organizationId!, params),
      ticketClosureOutcomesData: this.socDataService.fetchTicketClosureOutcomesData(this.organizationId!, params),
      ticketCategoryPerformanceData: this.socDataService.fetchTicketCategoryPerformanceData(
        this.organizationId!,
        params
      ),
      adrManualData: this.socDataService.fetchAdrManualData(this.organizationId!, params),
      ticketClosureData: this.socDataService.fetchTicketClosureBreakdownData(this.organizationId!, params),
      dispositionData: this.socDataService.fetchDisposition(this.organizationId!, params),
      outcomeStatusData: this.socDataService.fetchOutcomeStatus(this.organizationId!, params),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isPhase3Loading.set(false);
          // Start Phase 4 after Phase 3 completes
          this.loadPhase4Data(params);
        })
      )
      .subscribe({
        next: (response) => {
          this.automatedResponseUsageData.set(response.automatedResponseUsageData || []);
          this.ticketClosureOutcomesData.set(response.ticketClosureOutcomesData || []);
          this.ticketCategoryPerformanceData.set(response.ticketCategoryPerformanceData || []);
          this.adrManualData.set(response.adrManualData);
          this.ticketClosureData.set(response.ticketClosureData);
          this.dispositionData.set(response.dispositionData);
          this.outcomeStatusData.set(response.outcomeStatusData);
        },
        error: (error) => {
          console.error('Error loading Phase 3 data:', error);
          // Continue to Phase 4 even if Phase 3 fails
          this.loadPhase4Data(params);
        },
      });
  }

  /**
   * Phase 4: Top Detections + Suspect Users/Assets + Geographic Distribution
   * Loads detailed drill-down data
   */
  private loadPhase4Data(params: SocAnalyticsParams): void {
    this.isPhase4Loading.set(true);

    forkJoin({
      topDetectionsData: this.socDataService.fetchTopDetectionsData(this.organizationId!, params),
      topSuspectUsers: this.socDataService.fetchTopSuspectUsers(this.organizationId!, params),
      topSuspectAssets: this.socDataService.fetchTopSuspectAssets(this.organizationId!, params),
      topDetections: this.socDataService.fetchTopDetections(this.organizationId!, params),
      sourceMap: this.socDataService.fetchSourceMap(this.organizationId!, params),
      destinationMap: this.socDataService.fetchDestinationMap(this.organizationId!, params),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isPhase4Loading.set(false);
        })
      )
      .subscribe({
        next: (response) => {
          this.topDetectionsData.set(response.topDetectionsData);
          this.topSuspectUsersData.set(response.topSuspectUsers);
          this.topSuspectAssetsData.set(response.topSuspectAssets);
          this.topUserDetailData.set(response.topDetections);
          this.topAssetDetailData.set([]);
          this.sourceMapData.set(response.sourceMap);
          this.destinationMapData.set(response.destinationMap);
        },
        error: (error) => {
          console.error('Error loading Phase 4 data:', error);
        },
      });
  }

  // NEW: Event handlers for the reusable analytics filter component
  public onTimeRangeChange(selectedRange: TimeRange): void {
    this.selectedTimeRange.set(selectedRange);
    // Update string properties when time range changes
    this.updateDateStrings();
    // Removed applyFilters() call - it will only be called by the button now
  }

  // Helper method to update date string properties with proper timezone formatting
  private updateDateStrings(): void {
    const startDateValue = this.startDate();
    const endDateValue = this.endDate();

    if (startDateValue && endDateValue) {
      // Create new Date objects to avoid mutating the originals
      // Normalize to UTC day boundaries
      const startDate = new Date(startDateValue);
      startDate.setUTCHours(0, 0, 0, 0);
      this.startDateString = startDate.toISOString();

      const endDate = new Date(endDateValue);
      endDate.setUTCHours(23, 59, 59, 999);
      this.endDateString = endDate.toISOString();

      // Update dateRangeArray for child components
      this.dateRangeArray = [this.startDateString, this.endDateString];
    } else {
      this.startDateString = '';
      this.endDateString = '';
      this.dateRangeArray = [];
    }
  }

  // Update startDate when the date picker value changes
  public onStartDateChange(newDate: Date | null): void {
    if (newDate) {
      // Store the original date in the signal
      this.startDate.set(newDate);
      // Normalize to UTC day boundary for API calls
      const normalizedDate = new Date(newDate);
      normalizedDate.setUTCHours(0, 0, 0, 0);
      this.startDateString = normalizedDate.toISOString();
    } else {
      this.startDate.set(null);
      this.startDateString = '';
    }
    // No API call here
  }

  // Update endDate when the date picker value changes
  public onEndDateChange(newDate: Date | null): void {
    if (newDate) {
      // Store the original date in the signal
      this.endDate.set(newDate);
      // Normalize to UTC day boundary for API calls
      const normalizedDate = new Date(newDate);
      normalizedDate.setUTCHours(23, 59, 59, 999);
      this.endDateString = normalizedDate.toISOString();
    } else {
      this.endDate.set(null);
      this.endDateString = '';
    }
    // No API call here
  }

  public onApplyFilters(_filterState: FilterState): void {
    // Apply filters and refresh data
    this.applyFilters();
  }

  private loadProducts(): void {
    if (!this.organizationId) {
      return;
    }

    (this.productService as any).setOrganizationId(this.organizationId);
    (this.productService as any).read((response: any) => {
      console.log('Products loaded for SOC Security Tools filter:', response.data);
      this.products.set(response.data || []);
    });
  }

  public onToolsChange(tools: string[]): void {
    this.selectedTools.set(tools);
  }

  /**
   * Convert selected tools to security_tool parameter format for backend API calls
   */
  public getSecurityToolsParameter(): string | undefined {
    const tools = this.selectedTools();
    if (tools.length === 0) {
      return undefined;
    }

    // Return product IDs format: 2,3
    return tools.map((tool: any) => tool.id || tool).join(',');
  }

  // Open Ticket dialog when the Events vs Escalations chart is clicked
  public onEventsToEscalationsChartClick(): void {
    // Ensure we have dates; fall back to signals if strings not set yet
    let start = this.startDateString;
    let end = this.endDateString;
    if ((!start || !end) && this.startDate() && this.endDate()) {
      const s = new Date(this.startDate()!);
      s.setHours(0, 0, 0, 0);
      start = s.toISOString();
      const e = new Date(this.endDate()!);
      e.setHours(23, 59, 59, 999);
      end = e.toISOString();
      this.startDateString = start;
      this.endDateString = end; // cache
    }

    if (!this.organizationId || !start || !end) {
      console.warn('Missing data for Events/Escalations dialog', { org: this.organizationId, start, end });
      return;
    }

    this.showEventsEscalationsDialog.set(true);
  }

  public onEventsEscalationsDialogClose(): void {
    this.showEventsEscalationsDialog.set(false);
  }

  // SLA Adherence Dialog Methods
  public onSlaCardClick(): void {
    if (!this.organizationId || !this.startDateString || !this.endDateString) {
      return; // Don't allow click if no org ID or dates
    }

    this.slaGridService.url = `${environment.apiUrl}/v1/analytics/administrative/slaadherencemetricsdetails(${this.organizationId})`;
    this.slaGridService.appendQueryString = `org_ids=${this.organizationId}&type=filtered&current_start=${this.startDateString}&current_end=${this.endDateString}`;
    this.slaGridService.read();
    this.showSlaPopup.set(true);
  }

  public closeSlaPopup(): void {
    this.showSlaPopup.set(false);
  }

  /**
   * Opens alert dialog with MTTD configuration values
   */
  onOpenAlertDialogMTTD(): void {
    let demostr = checkIsDemoOrg()? "/demo": "";
    const config: AlertDataDialogConfig = {
      organizationId: this.organizationId,
      startDate: this.startDateString,
      endDate: this.endDateString,
      queryType: QueryType.Filtered,
      selectedOrgIds: this.organizationId ? [this.organizationId] : [],
      event: 'raw_events',
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/alerteventsdetails/`,
      visibleColumns: this.socAlertVisibleColumns,
      securityTool: this.getSecurityToolsParameter(),
      showExcelExport: false,
      reportTypeKey: 'mttd-disposition-stats-soc-report',
    };

    this.alertDialogConfig.set(config);
    this.showAlertDialog.set(true);
  }

  /**
   * Opens alert dialog with SLA configuration values
   */
  onOpenAlertDialogSLA(): void {
    let demostr = checkIsDemoOrg()? "/demo": "";
    const config: AlertDataDialogConfig = {
      organizationId: this.organizationId,
      startDate: this.startDateString,
      endDate: this.endDateString,
      queryType: QueryType.Filtered,
      selectedOrgIds: this.organizationId ? [this.organizationId] : [],
      event: 'raw_events',
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/alerteventsdetails/`,
      visibleColumns: this.socAlertVisibleColumns,
      securityTool: this.getSecurityToolsParameter(),
      showExcelExport: false,
      reportTypeKey: 'sla-adherence-soc-report',
    };

    this.alertDialogConfig.set(config);
    this.showAlertDialog.set(true);
  }

  /**
   * Opens ticket dialog with MTTR configuration values
   */
  onOpenTicketDialogMTTR(): void {
    let demostr = checkIsDemoOrg()? "/demo": "";
    const config: TicketDataDialogConfig = {
      organizationId: this.organizationId,
      startDate: this.startDateString,
      endDate: this.endDateString,
      queryType: QueryType.Filtered,
      selectedOrgIds: this.organizationId ? [this.organizationId] : [],
      event: 'escalations',
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/ticketeventsdetails/`,
      useDifferentDateField: true, // Use ticket_closed_time
      visibleColumns: this.socTicketVisibleColumns,
      securityTool: this.getSecurityToolsParameter(),
      showExcelExport: false,
      reportTypeKey: 'mttr-disposition-stats-soc-report',
      additionalParams: {
        '$filter':
          "department_name eq 'SOC Incidents' and " +
          "ticket_type eq 'Outbound' and " +
          "ticket_closed_time ne null and " +
          "created_time ne null and (" +
            "startswith(ticket_status, 'FP - ') or " +
            "startswith(ticket_status, 'UK - ') or " +
            "startswith(ticket_status, 'TP - ') or " +
            "startswith(ticket_status, 'CI - ')" +
          ")"
      }
    };

    this.ticketDialogConfig.set(config);
    this.showTicketDialog.set(true);
  }

  /**
   * Handles alert dialog close
   */
  onAlertDialogClose(): void {
    this.showAlertDialog.set(false);
    this.alertDialogConfig.set(null);
  }

  /**
   * Handles ticket dialog close
   */
  onTicketDialogClose(): void {
    this.showTicketDialog.set(false);
    this.ticketDialogConfig.set(null);
  }

  public allSlaData = (): Observable<GridDataResult> => {
    return this.slaGridService.queryAll();
  };

  // PDF Export method to be called by parent
  public onParentPdfExport(filename: string): void {
    console.log('SOC component received PDF export request with filename:', filename);
    if (this.socPdf) {
      this.socPdf.saveAs(filename);
    } else {
      console.error('SOC PDF export component not found');
    }
  }
}
